import React, { useEffect, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { AppState } from '@chegg-wt/core/state/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { ONE_AUTH_MODAL, UPGRADE_MODAL } from '@chegg-wt/core/state/modals/constants'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { openModal, closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  writingFormSubmit,
  formSubmitStarted,
  formSubmitStopped,
} from '@chegg-wt/core/state/form/actions'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import { currentStyleNameSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { removeUpgradeCitationFromSessionStorage } from '@chegg-wt/core/utils/upgrade'
import { Config } from '@chegg-wt/core/config'
import { useRoutesContext } from '../../RoutesContext'
import AppLink from '../../AppLink'
import { AuthModalData, AuthModalOrigin } from '../AuthModal/types'
import {
  FOOTER_BANNER_CTA_TEXT,
  FOOTER_BANNER_TEXT,
  UCF_UPGRADE_MODAL_ID,
  FREE_CTA_TEXT,
  UPGRADE_MODAL_TITLE,
  FREE_STYLE_TEXT,
} from './constants'
import { HackyModal, ModalContents, FreeCTA, HackyFooter } from './styled'
import { SmallNotificationBanner } from './SmallNotificationBanner'
import { FREE_CTA_DATA_TEST } from './testIds'

interface UCFUpgradeModalProps {
  isLoggedIn: boolean
  modalData: {
    withAuthBanner: boolean
    alwaysParticipate?: boolean
  }
  freeStyle: string
  openModal: (modalOptions: OpenModalPayload) => void
  closeModal: (name: string, origin?: string) => void
  writingFormSubmit: ({ citation, initialCitationData }) => void
  formSubmitStopped: () => void
  formSubmitStarted: () => void
}

const MODAL_ANIMATION_DURATION_MS = 300

export const UCFUpgradeModal: React.FC<UCFUpgradeModalProps> = ({
  isLoggedIn,
  openModal,
  closeModal,
  modalData,
  formSubmitStopped,
  formSubmitStarted,
}) => {
  const { currentStyleName } = Config
  const { toUpgrade } = useRoutesContext()
  const [isOpen, setIsOpen] = useState(true)
  const [isSAModalOpen, setIsSAModalOpen] = useState(false)

  const onSignInClick = () => {
    formSubmitStarted()

    setIsOpen(false)
    setIsSAModalOpen(true)
  }

  useEffect(() => {
    const isContinuing = isSAModalOpen
    const isBailingOut = !isOpen && !isContinuing
    if (isBailingOut) {
      formSubmitStopped()
    }
  }, [isOpen, isSAModalOpen])

  useEffect(() => {
    if (!isSAModalOpen) {
      return
    }

    const asyncOpenId = setTimeout(() => {
      const data: AuthModalData = {
        defaultFlow: 'signin',
        origin: AuthModalOrigin.UPGRADE_MODAL,
        alwaysParticipate: modalData.alwaysParticipate,
      }
      openModal({ name: ONE_AUTH_MODAL, data })
    }, MODAL_ANIMATION_DURATION_MS)

    return () => {
      clearTimeout(asyncOpenId)
    }
  }, [isSAModalOpen])

  useEffect(() => {
    if (isOpen) {
      return
    }

    const asyncCloseId = setTimeout(
      () => !isSAModalOpen && closeModal(UPGRADE_MODAL),
      MODAL_ANIMATION_DURATION_MS
    )

    return () => {
      clearTimeout(asyncCloseId)
    }
  }, [isOpen])

  const closeHackyModal = useCallback(() => {
    setIsOpen(false)
    removeUpgradeCitationFromSessionStorage()
  }, [])

  return (
    <ClientOnlyPortal>
      <HackyModal
        id={UCF_UPGRADE_MODAL_ID}
        isOpen={isOpen}
        onClose={closeHackyModal}
        shouldCloseOnClickOutside={false}
        title={UPGRADE_MODAL_TITLE}
      >
        <>
          <ModalContents>
            <AppLink route={toUpgrade()}>
              <FreeCTA
                href="I'm needed because TS complains otherwise, AppLink replaces me when rendering"
                data-test={FREE_CTA_DATA_TEST}
              >
                {FREE_CTA_TEXT}
              </FreeCTA>
              <span>
                {FREE_STYLE_TEXT} {currentStyleName}
              </span>
            </AppLink>
          </ModalContents>
          {modalData.withAuthBanner && !isLoggedIn ? (
            <HackyFooter>
              <SmallNotificationBanner
                message={FOOTER_BANNER_TEXT}
                ctaText={FOOTER_BANNER_CTA_TEXT}
                onSignInClick={onSignInClick}
              />
            </HackyFooter>
          ) : null}
        </>
      </HackyModal>
    </ClientOnlyPortal>
  )
}

const mapStateToProps = (state: AppState) => ({
  isLoggedIn: loggedInSelector(state),
  modalData: modalDataSelector(state),
  freeStyle: currentStyleNameSelector(state),
})

const mapDispatchToProps = {
  openModal,
  closeModal,
  writingFormSubmit,
  formSubmitStopped,
  formSubmitStarted,
}

export default connect(mapStateToProps, mapDispatchToProps)(UCFUpgradeModal)
